import React from 'react';
import { TopNavbar } from './TopNavbar';
import { JobDetailsPanel } from './JobDetailsPanel';
import { matchPath, Navigate, useParams } from 'react-router';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { EmptyState } from '../../components/EmptyState';
import { JobPageTab } from './JobPageTab';
import { AccountIntegration } from '../../entities/AccountIntegration';
import { QueryClientProvider } from 'react-query';
import { queryClient } from '../../config/queryClient';
import { useJobDetails } from '../../queries/useJobDetails';
import useCurrentUser from '../../queries/useCurrentUser';
import useCurrentOrganization from '../../queries/v1/useCurrentOrganization';

interface PropTypes {
  activeTab: JobPageTab;
  atsSignableOfferEnabled: boolean;
  atsEditBeforeExportToHrisEnabled: boolean;
  emailAccountIntegration: AccountIntegration;
  jobBoardEnabled: boolean;
  duplicateJobEnabled: boolean;
  standardNpsCalculation: boolean;
  selfSchedulingLinkOptionsEnabled: boolean;
  offerRequisitionFlowEnabled: boolean;
}

export const JobPage = (props: PropTypes) => {
  return (
    <QueryClientProvider client={queryClient}>
      <Body {...props} />
    </QueryClientProvider>
  );
};

function Body(props: PropTypes) {
  const jobId = useParams().id;
  const {
    data: jobDetails,
    isLoading: isLoadingJobDetails,
    isError: isJobDetailsError,
  } = useJobDetails(jobId);
  const {
    data: currentUser,
    isLoading: isLoadingCurrentUser,
    isError: isCurrentUserError,
  } = useCurrentUser();
  const {
    data: organization,
    isLoading: isLoadingOrganization,
    isError: isOrganizationError,
  } = useCurrentOrganization(['organization_info']);

  const isLoading =
    isLoadingJobDetails || isLoadingCurrentUser || isLoadingOrganization;
  const loadingFailed =
    isJobDetailsError || isCurrentUserError || isOrganizationError;

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (loadingFailed) {
    return (
      <EmptyState
        title='We have encountered an error'
        text='Please try again. If the error persists contact our team at support@screenloop.com'
        cardless={true}
      />
    );
  }

  if (
    !props.jobBoardEnabled &&
    matchPath('/jobs/:id/paid_job_boards', window.location.pathname)
  ) {
    return <Navigate to={`/jobs/${jobId}/pipeline`} />;
  }

  return (
    <>
      <TopNavbar
        key={`top-bar-${jobId}`}
        currentUser={currentUser!}
        jobDetails={jobDetails!}
        subdomain={organization!.subdomain}
        duplicateJobEnabled={props.duplicateJobEnabled}
      />
      <JobDetailsPanel
        key={`details-${jobId}`}
        activeTab={props.activeTab}
        atsSignableOfferEnabled={props.atsSignableOfferEnabled}
        currentUser={currentUser!}
        emailAccountIntegration={props.emailAccountIntegration}
        jobBoardEnabled={props.jobBoardEnabled}
        jobDetails={jobDetails!}
        organization={organization!}
        standardNpsCalculation={props.standardNpsCalculation}
        selfSchedulingLinkOptionsEnabled={
          props.selfSchedulingLinkOptionsEnabled
        }
        offerRequisitionFlowEnabled={props.offerRequisitionFlowEnabled}
      />
    </>
  );
}
